/**
 * Notes feature: load/save per slide, indicator, status.
 */

import { on, EVENTS } from '../core/events';
import type { LecturaHostAdapter, LecturaUser, DrawerView, NotesStorage } from '../core/types';
import { isRevealPrintPdfMode } from '../reveal-print-core';

const DEBOUNCE_MS = 500;
const PDF_H_CACHE_PREFIX = 'lectura-pdf-slide-h:';
const NOTES_MAX_TEXT_LENGTH = 10000;

function sanitizeNoteText(raw: string | null | undefined): string {
  const text = String(raw || '').replace(/\u0000/g, '');
  return text.length > NOTES_MAX_TEXT_LENGTH ? text.slice(0, NOTES_MAX_TEXT_LENGTH) : text;
}

/** Same tab URL with `summary` + `print-pdf` so Reveal builds the Lectura handout view. */
function buildSummaryOverviewHref(): string {
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('summary', '1');
    u.searchParams.set('print-pdf', '1');
    return u.toString();
  } catch {
    return '';
  }
}

export function registerNotes(
  adapter: LecturaHostAdapter,
  drawerView: DrawerView,
  storage: NotesStorage
): void {
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;

  function getPresId(): string {
    return adapter.getPresId();
  }
  function getSlideIndex(): number {
    return adapter.getSlideIndex();
  }

  function setStatus(status: string, message?: string): void {
    const wrap = drawerView.getNotesStatus?.();
    const text = drawerView.getNotesStatusText?.();
    if (!wrap || !text) return;
    wrap.classList.remove('rf-notes-status-saving', 'rf-notes-status-saved', 'rf-notes-status-saved-idle', 'rf-notes-status-error');
    if (status === 'saving') {
      wrap.classList.add('rf-notes-status-saving');
      text.textContent = 'Saving…';
    } else if (status === 'saved') {
      wrap.classList.add('rf-notes-status-saved');
      text.textContent = 'Saved';
    } else if (status === 'saved-idle') {
      wrap.classList.add('rf-notes-status-saved-idle');
      text.textContent = 'Saved';
    } else if (status === 'error') {
      wrap.classList.add('rf-notes-status-error');
      text.textContent = message || "Couldn't save";
    } else {
      text.textContent = '';
    }
  }

  function updateIndicator(): void {
    const indicator = drawerView.getDrawerTabNotesIndicator?.();
    if (!indicator) return;
    const user = adapter.getCurrentUser();
    if (!user?.uid || user.isAnonymous) {
      indicator.classList.add('rf-drawer-tab-notes-empty');
      indicator.classList.remove('rf-drawer-tab-notes-has');
      return;
    }
    const note = storage.getNote(getPresId(), getSlideIndex(), user.uid);
    const hasNote = (note.text || '').trim().length > 0;
    indicator.classList.toggle('rf-drawer-tab-notes-empty', !hasNote);
    indicator.classList.toggle('rf-drawer-tab-notes-has', hasNote);
    indicator.setAttribute('aria-label', hasNote ? 'Has note for this slide' : 'No note for this slide');
  }

  function flushSave(): void {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    const user = adapter.getCurrentUser();
    if (!user?.uid || user.isAnonymous) return;
    const textarea = drawerView.getNotesTextarea?.() as HTMLTextAreaElement | null | undefined;
    if (!textarea) return;
    const text = sanitizeNoteText(textarea.value || '').trim();
    if (textarea.value !== text) textarea.value = text;
    Promise.resolve(storage.setNote(getPresId(), getSlideIndex(), user.uid, { text }))
      .then(() => {
        setStatus('saved');
        setTimeout(() => setStatus('saved-idle'), 2500);
        updateIndicator();
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        setStatus('error', msg || "Couldn't save");
      });
  }

  function scheduleSave(): void {
    updateIndicator();
    if (saveTimeout) clearTimeout(saveTimeout);
    setStatus('saving');
    saveTimeout = setTimeout(flushSave, DEBOUNCE_MS);
  }

  function loadNoteForSlide(slideIndex: number): void {
    const user = adapter.getCurrentUser();
    const textarea = drawerView.getNotesTextarea?.() as HTMLTextAreaElement | null | undefined;
    if (!textarea) return;
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    setStatus('');
    if (!user?.uid || user.isAnonymous) {
      textarea.value = '';
      updateIndicator();
      return;
    }
    if (storage.getNoteAsync) {
      storage.getNoteAsync(getPresId(), slideIndex, user.uid)
        .then((note) => {
          if (adapter.getSlideIndex() !== slideIndex) return;
          textarea.value = sanitizeNoteText(note.text || '');
          updateIndicator();
        })
        .catch(() => {
          textarea.value = '';
          updateIndicator();
        });
      return;
    }
    const note = storage.getNote(getPresId(), slideIndex, user.uid);
    textarea.value = sanitizeNoteText(note.text || '');
    updateIndicator();
  }

  on(EVENTS.SLIDE_CHANGED, () => {
    loadNoteForSlide(adapter.getSlideIndex());
  });

  on(EVENTS.USER_CHANGED, () => {
    loadNoteForSlide(getSlideIndex());
    updateIndicator();
  });

  const textarea = drawerView.getNotesTextarea?.();
  if (textarea) {
    textarea.addEventListener('input', scheduleSave);
  }

  const summaryBtn = drawerView
    .getNotesEditorWrap?.()
    ?.querySelector('.rf-notes-summary-btn') as HTMLButtonElement | null;
  if (summaryBtn) {
    summaryBtn.setAttribute('aria-label', 'Open printable summary in a new tab');
    if (isRevealPrintPdfMode()) {
      summaryBtn.classList.add('rf-hidden');
    } else {
      summaryBtn.addEventListener('click', () => {
        const href = buildSummaryOverviewHref();
        if (!href) return;
        window.open(href, '_blank', 'noopener,noreferrer');
      });
    }
  }

  loadNoteForSlide(getSlideIndex());

  /**
   * Cache horizontal slide indices (Firestore keys match adapter.getSlideIndex / getIndices().h).
   * After Reveal PDF layout, getIndices() breaks — session cache from a normal deck view fixes mapping.
   */
  if (typeof Reveal !== 'undefined' && typeof Reveal.on === 'function' && !isRevealPrintPdfMode()) {
    Reveal.on('ready', () => {
      try {
        const slides = Reveal.getSlides?.() ?? [];
        const hs = slides.map((s) => {
          const ix = Reveal.getIndices?.(s);
          return typeof ix?.h === 'number' ? ix.h : 0;
        });
        sessionStorage.setItem(PDF_H_CACHE_PREFIX + getPresId(), JSON.stringify(hs));
      } catch {
        /* ignore */
      }
    });
  }

  registerPdfExportPersonalNotes(adapter, storage);
}

function parsePdfSlideNumberBase(page: Element): number {
  const el = page.querySelector('.slide-number-pdf');
  const raw = (el?.textContent || '').trim();
  const base = raw.split('.')[0].trim();
  const n = parseInt(base, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function getCachedHorizontalIndices(presId: string): number[] | null {
  try {
    const raw = sessionStorage.getItem(PDF_H_CACHE_PREFIX + presId);
    if (!raw) return null;
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return null;
    return arr.map((x) => (typeof x === 'number' ? x : 0));
  } catch {
    return null;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function personalNoteHtml(text: string): string {
  const lines = String(text || '').split(/\n/);
  if (lines.length === 0) return '<p class="lectura-print-note-empty">—</p>';
  return lines
    .map((line) => `<p>${line.trim().length ? escapeHtml(line) : '&nbsp;'}</p>`)
    .join('');
}

/**
 * Replace Reveal speaker-notes-pdf blocks with Firestore personal notes for the logged-in user.
 * Runs on pdf-ready after installRevealPrintHooks (strip codewords / layout).
 */
function registerPdfExportPersonalNotes(adapter: LecturaHostAdapter, storage: NotesStorage): void {
  if (typeof Reveal === 'undefined' || typeof Reveal.on !== 'function') return;

  if (!storage.getNoteAsync) {
    Reveal.on('pdf-ready', () => {
      window.setTimeout(() => {
        const pages = Array.from(document.querySelectorAll('.reveal .slides .pdf-page'));
        pages.forEach((page) => {
          (page as HTMLElement).setAttribute('data-lectura-has-personal-note', '0');
        });
        window.dispatchEvent(
          new CustomEvent('lectura-print-personal-notes-hydrated', {
            detail: { totalSlides: pages.length, withPersonalNotes: 0 }
          })
        );
      }, 50);
    });
    return;
  }

  Reveal.on('pdf-ready', () => {
    void hydratePdfPersonalNotes(adapter, storage);
  });
}

/**
 * pdf-ready often fires before Firebase onAuthStateChanged delivers the signed-in session.
 * Wait for a non-anonymous user, or until anonymous persists long enough to treat as guest.
 */
async function resolveUserForPdfExport(adapter: LecturaHostAdapter): Promise<LecturaUser | null> {
  const deadline = Date.now() + 8000;
  let anonSince: number | null = null;

  while (Date.now() < deadline) {
    const u = adapter.getCurrentUser();
    if (u?.uid && !u.isAnonymous) return u;
    if (!u?.uid) {
      anonSince = null;
      await new Promise((r) => setTimeout(r, 40));
      continue;
    }
    if (u.isAnonymous) {
      if (anonSince === null) anonSince = Date.now();
      if (Date.now() - anonSince > 1500) return u;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  return adapter.getCurrentUser();
}

async function hydratePdfPersonalNotes(adapter: LecturaHostAdapter, storage: NotesStorage): Promise<void> {
  const presId = adapter.getPresId();
  const user = await resolveUserForPdfExport(adapter);
  const cache = getCachedHorizontalIndices(presId);
  const pages = Array.from(
    document.querySelectorAll('.reveal .slides .pdf-page')
  ) as HTMLElement[];

  const tasks = pages.map(async (page, pageIndex) => {
    const notesEl = page.querySelector('.speaker-notes-pdf') as HTMLElement | null;
    if (!notesEl) {
      page.setAttribute('data-lectura-has-personal-note', '0');
      return;
    }

    const hasNum = page.querySelector('.slide-number-pdf');
    const idx = hasNum ? parsePdfSlideNumberBase(page) - 1 : pageIndex;
    const h = typeof cache?.[idx] === 'number' ? cache[idx]! : idx;

    if (!user?.uid || user.isAnonymous) {
      notesEl.classList.add('lectura-print-notes--personal');
      notesEl.innerHTML =
        '<p class="lectura-print-notes-login-hint">Sign in to export your personal notes (saved per slide in the drawer).</p>';
      page.setAttribute('data-lectura-has-personal-note', '0');
      return;
    }

    let text = '';
    try {
      const note = await storage.getNoteAsync!(presId, h, user.uid);
      text = sanitizeNoteText(note.text || '');
    } catch {
      text = '';
    }

    notesEl.classList.add('lectura-print-notes--personal');
    notesEl.innerHTML = personalNoteHtml(text);
    page.setAttribute('data-lectura-has-personal-note', text.trim().length > 0 ? '1' : '0');
  });

  await Promise.all(tasks);
  document.documentElement.classList.add('lectura-print-personal-notes');

  let withPersonalNotes = 0;
  for (const p of pages) {
    if (p.getAttribute('data-lectura-has-personal-note') === '1') withPersonalNotes += 1;
  }
  window.dispatchEvent(
    new CustomEvent('lectura-print-personal-notes-hydrated', {
      detail: { totalSlides: pages.length, withPersonalNotes }
    })
  );
}
